/**
 * Web Component for custom load more pagination functionality.
 * 
 * This web component is responsible for loading in pages using the Shopify pages API. Its core functionalities include:
 *     - Handling page generation/removal via the API and window.history
 *     - Updating the URL to match the page being scrolled over for SEO and link sharing
 *     - Load Previous & Load More functionality
 */

class LoadMore extends HTMLElement 
{
    // IDs
    static gridContainer = 'ProductGridContainer';
    static grid = 'product-loop';
    static loadMoreButton = 'LoadMoreButton';
    static loadMoreText = 'LoadMoreText';
    static loadPrevButton = 'LoadPreviousButton';

    // Classes
    static loadButtons = 'pagination-load-button';
    static paginationWrapper = 'filter-form__sort-pagination';
    static sortBy = 'filter-form__sort';

    constructor (button = null, previous)
    {
        super();

        this.debouncedOnClick = debounce((event) => {
            if (previous)
                this.onLoadPreviousRequested(event);
            else
                this.onLoadMoreRequested(event);
        }, 500);
        
        const loadMoreButton = this.querySelector(`#${button ? button : LoadMore.loadMoreButton}`);
        loadMoreButton.addEventListener('click', this.debouncedOnClick.bind(this));

        // Setup the initial and previous pointers using the starting page
        LoadMore.pageNumberInitial = parseInt(loadMoreButton.getAttribute('data-initial-page'));
        LoadMore.pageNumberPrev = parseInt(loadMoreButton.getAttribute('data-initial-page'));
        LoadMore.pageNumberMax = parseInt(loadMoreButton.getAttribute('data-max-page'));

        LoadMore.pageList = [LoadMore.pageNumberInitial];

        // Attach the loading buttons to the class
        LoadMore.loadMore = document.getElementById(LoadMore.loadMoreButton);
        LoadMore.loadPrev = document.getElementById(LoadMore.loadPrevButton);
    }

    /**
     * Returns whether a browser is Webkit-based or not.
     * 
     * @returns {boolean} Whether the browser is Webkit-based.
     */
    static isWebkitBrowser ()
    {
        return /Safari/.test(navigator.userAgent) && /Apple Computer/.test(navigator.vendor);
    }

    /**
     * Responsible for storing the target element on a collection page, so the 
     * user's original position can be restored when navigating back in the browser history.
     */
    static setProductGridListeners ()
    {
        var productGrid = document.getElementById(LoadMore.grid);
        if (productGrid && typeof productGrid !== 'undefined') {
            let targetElement = null;

            // Stores the ID of the element to scroll to when loading a collections page.
            const storeScrollElement = (targetElement, clickTarget) => {
                // Ensure the user is clicking an element in the product index card
                if (clickTarget !== targetElement && clickTarget.tagName !== 'BUTTON') {
                    localStorage.setItem('load-more-scroll-pos', JSON.stringify({
                        id: targetElement.id,
                        position: targetElement.getBoundingClientRect().top * -1
                    }));
                    history.scrollRestoration = 'manual';
    
                    // Use setTimeout as it is non-blocking, so statements outside of it will
                    // be completed first - ensuring this assignment is completed after localStorage.
                    setTimeout(function () {
                        window.location.href = targetElement.querySelector('a').href;
                    });
                }
            }

            // Listen for clicks on the product grid and find which product was clicked.
            // If a product was selected, push its path to history and store the element.
            productGrid.addEventListener('click', function (event) {
                event.preventDefault();

                targetElement = event.target.closest('.product-index');
                if (targetElement && typeof targetElement !== 'undefined')
                    storeScrollElement(targetElement, event.target);
            });
        }
    }

    /**
     * Setup the various event listeners used by the component.
     */
    static setupListeners ()
    {
        let previousPage = null;

        // Add a scroll listener that updates the current page URL whenever a user scrolls over
        // one of the product count headers with the data-page-marker attribute
        document.addEventListener('scroll', () => {
            const scrolled = window.scrollY + window.innerHeight;
            const productCountHeaders = document.querySelectorAll('[data-page-marker]');

            let page = null;
            // Loop through the headers and check which one has been scrolled over. The deepest in the
            // DOM is assigned to the page variable.
            productCountHeaders.forEach(header => {
                const offsetTop = header.getBoundingClientRect().top + window.pageYOffset + header.offsetHeight;

                if (scrolled > offsetTop)
                    page = LoadMore.getPageMarker(header);
            });

            // If the page is not the existing one (as it updates with every scroll), set the URL.
            if (page !== previousPage) {
                // Get the current URL and create a URLSearchParams object from it
                const url = new URL(window.location.href);
                const searchParams = new URLSearchParams(url.search);

                if (page !== null && page !== undefined) {
                    searchParams.set('page', page);

                    // create a new URL with the updated query string and replace the current URL without reloading the page
                    const newUrl = `${url.origin}${url.pathname}?${searchParams.toString()}${url.hash}`;
                    history.replaceState({ path: newUrl }, '', newUrl);
                    console.log('Replacing state with:', newUrl);

                    previousPage = page;
                }
            }
        });
    }

    /**
     * Handles the user clicking the browser back-button.
     * 
     * The filters are applied via a fetch request, so any manual back-button presses need to
     * be intercepted and handled to simulate going back.
     */
    static setHistoryChangeListeners () 
    {
        // If the history changes, grab the page number and re-render (remove the old content)
        window.addEventListener('popstate', (event) => {
            // Fires for Safari/Webkit browsers that use popstate when going back in history
            var targetElement = LoadMore.restoreScrollPosition();

            // This check is here to ensure that the popstate is only for collection-page based back/forward traversal.
            // Safari doesn't reload pages when going forward/back so it calls popstate, while Chrome does reload so this method isn't called.
            if (!localStorage.getItem('load-more-scroll-complete')) {
                if (event.state && event.state.path && typeof event.state.path !== 'undefined') {
                    const url = new URL(event.state.path);
                    const urlParams = url.searchParams;

                    let eventPage = parseInt(urlParams.get('page'));

                    // Check if the target element is on the page which is being signalled for removal/addition as this signals
                    // an error with the localStorage/load-more-scroll-complete and should be intercepted and ignored.
                    if (targetElement && typeof targetElement !== 'undefined') {
                        var targetPage = targetElement.getAttribute('data-page-number');
                        if (targetPage && typeof targetPage !== 'undefined' && targetPage == eventPage)
                            return;
                    }
    
                    let page;
                    let remove;
                    if (LoadMore.pageList.includes(eventPage)) {
                        page = LoadMore.pageList.pop();
                        remove = true;
                    } else {
                        page = eventPage;
                        remove = false;
    
                        LoadMore.pageList.push(page);
                    }
    
                    LoadMore.fetchNextPage(page, remove, false);
                    LoadMore.pageNumberPrev = eventPage;
                }
            } else {
                localStorage.removeItem('load-more-scroll-complete');
            }
        });
    }

    /**
     * Renders the individual elements that make up the count and product grid.
     * 
     * @param {*} html The HTML to load in.
     * @param {*} event The event that fired the render.
     */
    static renderData (html, remove = false)
    {
        let container = document.getElementById(LoadMore.gridContainer);
        let prevProductCount = container.querySelector(`.${LoadMore.sortBy}`);

        // Render the product grid
        LoadMore.renderProductGridContainer(html, remove);

        // Re-apply hidden products logic to newly injected product grid HTML
        if (window.HiddenProducts && typeof HiddenProducts.hide === 'function')
            HiddenProducts.hide();

        // If loading a previous page, render the product count
        if (LoadMore.pageNumberPrev <= LoadMore.getLowestVisiblePage())
            LoadMore.renderProductCount(html, remove && prevProductCount ? prevProductCount.outerHTML : null);

        // Toggle the loading classes
        LoadMore.toggleLoading();

        if (LoadMore.loadPrev && typeof LoadMore.loadPrev !== 'undefined') {
            // Hide the "Load More" button if the user has reached the first results page
            if (LoadMore.pageNumberPrev == 1 || LoadMore.pageNumberInitial == 1)
                LoadMore.loadPrev.style.display = 'none';
            else
                LoadMore.loadPrev.style.display = 'flex';
        }

        if (LoadMore.loadMore && typeof LoadMore.loadMore !== 'undefined') {
            // Hide the "Load Previous" button if the user reached the last results page
            if (LoadMore.pageNumberPrev == LoadMore.pageNumberMax || LoadMore.pageNumberInitial == LoadMore.pageNumberMax)
                LoadMore.loadMore.parentElement.style.display = 'none';
            else
                LoadMore.loadMore.parentElement.style.display = 'flex';
        }

        // Get the page count header to jump to
        var pageMarker = LoadMore.pageNumberPrev;
        if (remove)
            pageMarker = LoadMore.pageList.length > 0 ? LoadMore.pageList.slice(-1)[0] : LoadMore.pageNumberInitial;

        var pageCountHeader = document.querySelector(`[data-page-marker="${pageMarker}"]`);
        if (pageCountHeader && typeof pageCountHeader !== 'undefined') {
            setTimeout(() => {
                // Jump to the page count header
                pageCountHeader.scrollIntoView({ block: 'start', inline: 'nearest' });
            }, 100);
        }
    }

    /**
     * Renders the page/section from the existing cache.
     * 
     * @param {*} filterDataUrl The URL for the cache to find.
     */
    static renderSectionFromCache (filterDataUrl, remove)
    {
        // Grab the HTML from the stored URL/HTML pairs in the cache.
        const html = LoadMore.loadData.find(filterDataUrl).html;

        LoadMore.renderData(html, remove);
    }

    /**
     * Renders the page/section from a fresh fetch of a URL.
     * 
     * @param {*} url The URL to fetch.
     */
    static renderSectionFromFetch (url) 
    {
        fetch(url)
            .then(response => response.text())
            .then((responseText) => {
                const html = responseText;

                // Cache the loadData so it can be accessed later if necessary.
                LoadMore.loadData = [...LoadMore.loadData, { html, url }];

                LoadMore.renderData(html);
            });
    }

    /**
     * Restores the user's scroll position in the collections page
     * if they've navigated back from a PLP using browser back/forward buttons.
     * 
     * @param {boolean} delay Whether or not to delay the return to position.
     */
    static restoreScrollPosition (delay = false)
    {
        var element = null;
        if (localStorage.getItem('load-more-scroll-pos')) {
            let scrollState = JSON.parse(localStorage.getItem('load-more-scroll-pos'));

            element = document.getElementById(scrollState.id);
            if (element && typeof element !== 'undefined') {
                setTimeout(function () {
                    element.scrollIntoView();
                    window.scrollBy(0, scrollState.position);
                }, delay ? 500 : 0);
            }

            localStorage.removeItem('load-more-scroll-pos');
            
            if (!delay)
                localStorage.setItem('load-more-scroll-complete', true);
        }

        return element;
    }

    /**
     * Restores the history.scrollRestoration value if the manual scroll has
     * been completed. The restoration takes place after 5 seconds to ensure
     * all browser-based scroll events have fired.
     */
    static resetScrollRestoration ()
    {
        if (history.scrollRestoration == 'manual' && !localStorage.getItem('load-more-scroll-pos')) {
            setTimeout(function () {
                history.scrollRestoration = 'auto';
            }, 5000);
        }
    }

    /**
     * Fetches the next page to be loaded in.
     * 
     * @param {*} page The current page.
     * @param {*} updateHistory Whether to update the history or not.
     */
    static fetchNextPage (page, removeSection, updateHistory = true)
    {
        console.log('Requested to load page', page, 'with remove set to', removeSection);

        // Disable fetch if the user is trying to load before page 1 or after the last page
        if (page <= 0 || page > LoadMore.pageNumberMax || (page == LoadMore.getLowestVisiblePage() && page == LoadMore.getHighestVisiblePage()))
            return;

        LoadMore.pageNumberPrev = page;

        let productGrid = document.getElementById(LoadMore.grid);

        if (productGrid && typeof productGrid !== 'undefined') {
            // Toggle the loading classes
            LoadMore.toggleLoading();

            // Grab the section ID and generate the URL to load from
            var sectionId = productGrid.dataset.id;

            const queryString = window.location.search;
            const urlParams = new URLSearchParams(queryString);
            urlParams.set('page', page);
            
            const allParamsString = urlParams.toString();

            const url = `${window.location.pathname}?section_id=${sectionId}&${allParamsString}`;
            const filterDataUrl = element => element.url === url;

            // Check whether filterDataUrl exists in the filterData array. If it does, the user has likely 
            // gone back in the history, so we load from the cache. Otherwise, we load the URL from scratch.
            LoadMore.loadData.some(filterDataUrl) ?
                LoadMore.renderSectionFromCache(filterDataUrl, removeSection) :
                LoadMore.renderSectionFromFetch(url);

            if (updateHistory)
                LoadMore.pushToHistory(page);
        }
    }

    /**
     * Pushes the current URL state to history.
     * 
     * @param {*} page The current page.
     */
    static pushToHistory (page)
    {
        LoadMore.pageList.push(page);

        const historyUrl = new URL(window.location.href);
        const searchParams = new URLSearchParams(historyUrl.search);
        searchParams.set('page', page);

        const newUrl = `${historyUrl.origin}${historyUrl.pathname}?${searchParams.toString()}${historyUrl.hash}`;
        history.pushState({ path: newUrl }, '', newUrl);
    }

    /**
     * Returns the highest header element in the DOM.
     * 
     * @returns The element
     */
    static getHighestHeaderElement ()
    {
        let markers = document.querySelectorAll('[data-page-marker]');

        const elements = Array.from(markers);
        const highestElement = elements.reduce((highest, current) => {
            return LoadMore.getPageMarker(highest) > LoadMore.getPageMarker(current) ? highest : current;
        });

        return highestElement;
    }

    /**
     * Returns the highest currently visible page.
     * 
     * @returns {int} The highest currently visible page number.
     */
    static getHighestVisiblePage ()
    {
        var highestElement = LoadMore.getHighestHeaderElement();

        return LoadMore.getPageMarker(highestElement);
    }

    /**
     * Returns the lowest currently visible page.
     * 
     * @returns {int} The lowest currently visible page number.
     */
    static getLowestVisiblePage ()
    {
        let markers = document.querySelectorAll('[data-page-marker]');

        const elements = Array.from(markers);
        const lowestElement = elements.reduce((lowest, current) => {
            return LoadMore.getPageMarker(lowest) < LoadMore.getPageMarker(current) ? lowest : current;
        });

        return LoadMore.getPageMarker(lowestElement);
    }

    /**
     * Returns the product count for the current pages loaded.
     * 
     * @returns {Object} Object containing the shown items and total.
     */
    static getProductCount ()
    {
        var highestElement = LoadMore.getHighestHeaderElement();
        var paginationWrapper = highestElement.querySelector(`.${LoadMore.paginationWrapper}`);

        return {
            'showing': paginationWrapper.getAttribute('data-items-count-current'),
            'total': paginationWrapper.getAttribute('data-items-count-total')
        };
    }

    /**
     * Returns the page marker value as an integer.
     * 
     * @param {*} element The element to fetch the value from.
     * @returns {int} The page marker integer value. 
     */
    static getPageMarker (element)
    {
        if (element.hasAttribute('data-page-marker'))
            return parseInt(element.getAttribute('data-page-marker'));

        return 0;
    }

    /**
     * Renders the product count container after the updated DOM is fetched.
     * 
     * @param {*} html The HTML to be used to replace the existing count container.
     */
    static renderProductCount (html, parsedHTML = null) 
    {
        // Grab the returned products count section HTML
        var parser = new DOMParser;
        
        if (!parsedHTML)
            parsedHTML = parser.parseFromString(html, 'text/html').querySelector(`.${LoadMore.sortBy}`).outerHTML;

        document.querySelector(`.${LoadMore.sortBy}`).outerHTML = parsedHTML;
    }

    /**
     * Renders the product grid container after the updated DOM is fetched.
     * 
     * @param {*} html The HTML to be used to replace the existing grid container content.
     * @param {boolean} remove Whether or not the elements should be removed or appended.
     */
    static renderProductGridContainer (html, remove)
    {
        var productGrid = document.getElementById(LoadMore.grid);
        if (productGrid && typeof productGrid !== 'undefined') {
            // Grab the returned products HTML
            var parser = new DOMParser;
            var parsedHTML = parser.parseFromString(html, 'text/html').getElementById(LoadMore.grid).innerHTML;
            var parsedResultsHTML = parser.parseFromString(html, 'text/html').querySelector(`.${LoadMore.sortBy}`).outerHTML;

            let highestPage = LoadMore.getHighestVisiblePage();
            let lowestPage = LoadMore.getLowestVisiblePage();

            if (remove) {
                // Convert the parsedHTML string into an array of elements
                let tempDiv = document.createElement('div');
                tempDiv.innerHTML = parsedHTML;
                let elementsToRemove = Array.from(tempDiv.children);

                // Remove the lowest header in the product grid (the one relevant to the items being removed)
                var countHeaders = productGrid.querySelectorAll(`.${LoadMore.sortBy}`);

                if (LoadMore.pageNumberPrev == lowestPage) {
                    // Remove the top header if loading previous
                    countHeaders[0].remove();
                } else if (LoadMore.pageNumberPrev == highestPage) {
                    // Remove the bottom header if loading more
                    countHeaders[countHeaders.length - 1].remove();
                }

                // Loop through the elements to remove and remove them from the product grid if they exist
                elementsToRemove.forEach(elementToRemove => {
                    let existingElement = productGrid.querySelector(`#${elementToRemove.id}`);

                    if (existingElement)
                        existingElement.remove();
                });
            } else {            
                if (LoadMore.pageNumberPrev <= lowestPage) {
                    var existingCountHeader = document.querySelector(`.${LoadMore.sortBy}`);
                    // Append the original product count header above the existing cards and below the new ones
                    productGrid.insertAdjacentElement('afterbegin', existingCountHeader.cloneNode(true));
                    // Append to the front/start of the product grid as we're loading a previous page
                    productGrid.insertAdjacentHTML('afterbegin', parsedHTML);
                } else if (LoadMore.pageNumberPrev > highestPage) {
                    // Add a page-marker attribute to the page header title
                    let tempDiv = document.createElement('div');
                    tempDiv.innerHTML = parsedResultsHTML;
                    tempDiv.children[0].setAttribute('data-page-marker', LoadMore.pageNumberPrev);

                    const childNodes = tempDiv.childNodes;
                    const serializedNodes = [];
                    for (let i = 0; i < childNodes.length; i++)
                        serializedNodes.push(childNodes[i].outerHTML);

                    // Append to the end of the product grid as we're loading the next page
                    productGrid.innerHTML += serializedNodes.join('');
                    // Append the product cards to the product grid
                    productGrid.innerHTML += parsedHTML;
                }
            }
        }
    }

    /**
     * Toggles the loading class that applies to the product grid and buttons
     * when aspects of the UI are updating during a fetch.
     */
    static toggleLoading ()
    {
        let productGrid = document.getElementById(LoadMore.grid);

        // Toggle the loading class for the product grid
        if (productGrid && typeof productGrid !== 'undefined')
            productGrid.classList.remove('loading');

        // Toggle the loading class on the buttons
        let buttons = document.querySelectorAll(`.${ LoadMore.loadButtons }`);
        buttons.forEach(button => {
            var defaultButtonContent = button.getAttribute('data-default-text');
            if (button.textContent == defaultButtonContent) {
                button.innerHTML = `<img class="loading-spinner" src="${button.getAttribute('data-loading-icon-src')}" width="32" height="32" />`;
            } else {
                button.innerHTML = defaultButtonContent;

                var loadMoreText = document.getElementById(LoadMore.loadMoreText);
                if (loadMoreText) {
                    var productCounts = LoadMore.getProductCount();
                    loadMoreText.textContent = `showing ${productCounts.showing} of ${productCounts.total}`;
                }
            }

            button.classList.toggle('loading');
        });
    }

    /**
     * Fires the fetchNextPage method to grab the next set of products. For example, if
     * the user is on page 4, this method will fetch page 5.
     * 
     * @param {*} event The event that triggered the fetch
     */
    onLoadMoreRequested (event)
    {
        event.preventDefault();
        LoadMore.fetchNextPage(LoadMore.getHighestVisiblePage() + 1, false, true);
    }

    /**
     * Fires the fetchNextPage method to grab the previous set of products. For example, if
     * the user is on page 4, this method will fetch page 3.
     * 
     * @param {*} event The event that triggered the fetch
     */
    onLoadPreviousRequested (event)
    {
        event.preventDefault();
        LoadMore.fetchNextPage(LoadMore.getLowestVisiblePage() - 1, false, true);
    }
}

customElements.define('load-more', LoadMore);

LoadMore.loadData = [];
LoadMore.setProductGridListeners();
LoadMore.setupListeners();
LoadMore.setHistoryChangeListeners();

// Listen for when the page loads and restore the history scrollRestoration to auto after a small
// delay to allow everything to load in (and the manual scroll to take precedence)
window.addEventListener('load', function () {
    // Restore scroll position for non-Webkit browsers as Webkit browsers use 'popstate' while
    // non-Webkit browsers do not, as such skip this step for Webkit browsers
    if (!LoadMore.isWebkitBrowser())
        LoadMore.restoreScrollPosition(true);

    // Reset the history.scrollRestoration value to 'auto'
    LoadMore.resetScrollRestoration();
});

class LoadPrevious extends LoadMore
{
    // IDs
    static loadPreviousButton = 'LoadPreviousButton';

    constructor ()
    {
        super(LoadPrevious.loadPreviousButton, true);
    }
}

customElements.define('load-previous', LoadPrevious);

LoadPrevious.loadData = [];